import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';
import { PROJECTS, SECTORS } from '../data/mock';
import {
  HeartPulse, Shield, Plane, Landmark, Building2,
  Sparkles, TreePine, Handshake, ChevronRight, MapPin
} from 'lucide-react';

const sectorIcons = {
  heartPulse: HeartPulse,
  shield: Shield,
  plane: Plane,
  landmark: Landmark,
  building2: Building2,
  sparkles: Sparkles,
  treePine: TreePine,
  handshake: Handshake,
};

export const ProjectsSection = () => {
  const [ref, inView] = useInView({ threshold: 0.1 });
  const [sectorsRef, sectorsInView] = useInView({ threshold: 0.1 });

  return (
    <>
      {/* Featured Projects */}
      <section id="projects" ref={ref} className="relative py-28 bg-[#FAFAF7]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="mb-16"
          >
            <span className="text-[#D4952B] text-sm font-semibold tracking-[0.2em] uppercase">Our Track Record</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0D1B2A] mt-3 leading-tight">
              Featured Projects
            </h2>
            <p className="text-2xl md:text-3xl text-[#0D1B2A]/30 font-semibold mt-2 font-['Noto_Sans_Arabic']">أبرز المشاريع المنفذة</p>
            <p className="text-[#1C2B33]/60 mt-4 max-w-xl text-base leading-relaxed">
              We are proud to support the Kingdom's vision and beyond. Our executed projects span the most ambitious developments in the region.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PROJECTS.map((project, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.08 }}
                className="group bg-white rounded-2xl p-6 border border-[#0D1B2A]/5 hover:border-[#D4952B]/25 hover:shadow-xl hover:shadow-amber-900/5 transition-all duration-500"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-[#0D1B2A]/5 group-hover:bg-[#D4952B]/10 flex items-center justify-center mb-4 transition-colors duration-500">
                      <MapPin className="w-5 h-5 text-[#0B3D5C] group-hover:text-[#D4952B] transition-colors" />
                    </div>
                    <h3 className="text-lg font-bold text-[#0D1B2A] group-hover:text-[#0B3D5C] transition-colors">
                      {project.name}
                    </h3>
                    <p className="text-[#1C2B33]/50 text-xs mt-1">{project.desc}</p>
                    <p className="text-[#0D1B2A]/30 text-[10px] font-medium mt-1 font-['Noto_Sans_Arabic'] line-clamp-2" dir="rtl">{project.nameAr}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#0D1B2A]/20 group-hover:text-[#D4952B] group-hover:translate-x-1 transition-all duration-300 mt-2" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Sectors */}
      <section ref={sectorsRef} className="relative py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={sectorsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="text-center mb-16 max-w-3xl mx-auto"
          >
            <span className="text-[#D4952B] text-sm font-semibold tracking-[0.2em] uppercase">Industries We Serve</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0D1B2A] mt-3 leading-tight">
              Key Sectors
            </h2>
            <p className="text-2xl md:text-3xl text-[#0D1B2A]/30 font-semibold mt-2 font-['Noto_Sans_Arabic']">القطاعات الرئيسية</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {SECTORS.map((sector, i) => {
              const Icon = sectorIcons[sector.icon];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={sectorsInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: 0.1 + i * 0.06 }}
                  className="group text-center p-6 rounded-2xl bg-[#FAFAF7] hover:bg-[#0D1B2A] border border-transparent hover:border-[#D4952B]/20 transition-all duration-500 cursor-default"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#0D1B2A]/5 group-hover:bg-[#D4952B]/20 flex items-center justify-center mx-auto mb-4 transition-colors duration-500">
                    <Icon className="w-5 h-5 text-[#0B3D5C] group-hover:text-[#E8B84A] transition-colors duration-500" />
                  </div>
                  <h3 className="text-sm font-bold text-[#0D1B2A] group-hover:text-white transition-colors duration-500">{sector.name}</h3>
                  <p className="text-[#0D1B2A]/30 group-hover:text-white/30 text-[10px] font-medium mt-0.5 transition-colors duration-500" dir="rtl">{sector.nameAr}</p>
                  <p className="text-[#1C2B33]/50 group-hover:text-white/50 text-xs mt-2 leading-relaxed transition-colors duration-500">{sector.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};
